import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { FilterConfig } from 'clickNgo/components/dataFilterBar'
import { fetchContactHPRoleOptions } from 'clickNgo/entities/contact'
import {
  fetchProfilingCodeOptions,
  ProfilingCode,
  ProfilingCodeType,
} from 'clickNgo/entities/profilingCode'
import { uniqueBy } from 'clickNgo/helpers/array'
import { selectCountries } from 'clickNgo/helpers/selectors'
import { NotificationController } from 'core-components/library/popup'

const contactProfilingTypes = [
  ProfilingCodeType.UserRights,
  ProfilingCodeType.Campaigns,
  ProfilingCodeType.Tools,
]

export const useFilterConfig = () => {
  const countries = useSelector(selectCountries)
  const [isHPRolesLoading, setIsHPRolesLoading] = useState(false)
  const [userRightsOptions, setUserRightsOptions] = useState<ProfilingCode[]>([])
  const [campaignsOptions, setCampaignsOptions] = useState<ProfilingCode[]>([])
  const [toolsOptions, setToolsOptions] = useState<ProfilingCode[]>([])
  const [hpRolesOptions, setHPRolesOptions] = useState<string[]>([])

  const filterConfig: FilterConfig[] = useMemo(
    () => [
      {
        id: 'contactId',
        label: 'Contact ID',
        type: 'text',
      },
      { id: 'firstName', label: 'First Name', type: 'text' },
      { id: 'lastName', label: 'Last Name', type: 'text' },
      { id: 'learnerId', label: 'Learner ID', type: 'text' },
      { id: 'email', label: 'Email', type: 'text' },
      {
        id: 'hpRoles',
        label: 'HP Roles',
        type: 'select',
        options: hpRolesOptions.map((role) => ({ label: role, value: role })),
      },
      {
        id: 'countryOfBusiness',
        label: 'Country of Business',
        type: 'select',
        options: countries.map((country) => ({
          label: country.name,
          value: country.value,
        })),
      },
      {
        id: 'mobilePhoneNumber',
        label: 'Mobile Phone',
        type: 'text',
      },
      {
        id: 'workPhoneNumber',
        label: 'Work Phone',
        type: 'text',
      },
      {
        id: 'userStatus',
        label: 'User Status',
        type: 'select',
        options: [
          {
            label: 'Contact',
            value: 'Contact',
          },
          {
            label: 'User',
            value: 'G_UPP_Partner_User',
          },
          {
            label: 'Admin User',
            value: 'G_UPP_Partner_Admin_User',
          },
        ],
      },
      {
        id: ProfilingCodeType.UserRights,
        label: 'User Rights',
        type: 'select',
        options: userRightsOptions.map((option) => ({
          label: option.codeName || option.code,
          value: option.code,
        })),
      },
      {
        id: ProfilingCodeType.Campaigns,
        label: 'Campaigns',
        type: 'select',
        options: campaignsOptions.map((option) => ({
          label: option.codeName || option.code,
          value: option.code,
        })),
      },
      {
        id: ProfilingCodeType.Tools,
        label: 'Tools',
        type: 'select',
        options: toolsOptions.map((option) => ({
          label: option.codeName || option.code,
          value: option.code,
        })),
      },
    ],
    [hpRolesOptions, countries, userRightsOptions, campaignsOptions, toolsOptions]
  )

  useEffect(() => {
    setIsHPRolesLoading(true)
    fetchProfilingCodeOptions(contactProfilingTypes).then((profilingCodeOptions) => {
      setUserRightsOptions(
        uniqueBy(profilingCodeOptions[ProfilingCodeType.UserRights], (item) => item.code)
      )
      setCampaignsOptions(
        uniqueBy(profilingCodeOptions[ProfilingCodeType.Campaigns], (item) => item.code)
      )
      setToolsOptions(uniqueBy(profilingCodeOptions[ProfilingCodeType.Tools], (item) => item.code))
    })
    fetchContactHPRoleOptions()
      .then((roles) => {
        setHPRolesOptions(roles)
      })
      .catch(() => {
        NotificationController.showError({
          message: 'Failed to load HP Role options',
        })
      })
      .finally(() => {
        setIsHPRolesLoading(false)
      })
  }, [])

  return { filterConfig, isLoading: isHPRolesLoading }
}
